import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import TranslatableContent from 'garaje/components/visitors/questionnaires/form/translatable-content';
import preventDefault from 'garaje/helpers/prevent-default';
import focusOnInsert from 'garaje/modifiers/focus-on-insert';
import onKeydown from 'garaje/modifiers/on-keydown';
import type { LocaleKey } from 'garaje/utils/locale-options';
import { localCopy } from 'tracked-toolbox';

interface VisitorsQuestionnairesFormTitleSignature {
  Args: {
    defaultLocale: LocaleKey;
    defaultLocaleTitle: string;
    readOnly?: boolean;
    selectedLocale: LocaleKey;
    title: string | null;
    update: (title: string) => void;
  };
}

export default class VisitorsQuestionnairesFormTitle extends Component<VisitorsQuestionnairesFormTitleSignature> {
  @tracked isEditing = false;
  @localCopy('args.title') title!: VisitorsQuestionnairesFormTitleSignature['Args']['title'];

  inputId = guidFor(this);

  @action
  cancel(): void {
    this.title = this.args.title;
    this.isEditing = false;
  }

  @action
  edit(): void {
    this.title = this.args.title;
    this.isEditing = true;
  }

  @action
  update(): void {
    const element = <HTMLInputElement>document.getElementById(this.inputId);
    this.args.update(element.value);
    this.isEditing = false;
  }

  <template>
    {{#if this.isEditing}}
      <div class="flex items-center">
        <TranslatableContent
          @defaultLocale={{@defaultLocale}}
          @selectedLocale={{@selectedLocale}}
          @showLanguagePill={{true}}
        >
          <:original>
            <span class="text-base font-normal pr-2" data-test-original-language-title>{{@defaultLocaleTitle}}</span>
          </:original>
          <:selected as |isTranslating|>
            <input
              type="text"
              class="mr-2 {{unless isTranslating '!mb-0 !w-1/3'}}"
              id={{this.inputId}}
              maxlength="50"
              value={{this.title}}
              {{focusOnInsert}}
              {{onKeydown "Enter" (preventDefault this.update)}}
              {{onKeydown "Escape" this.cancel}}
            />
          </:selected>
        </TranslatableContent>
        <button type="button" class="btn btn--outline mr-2" data-test-cancel-button {{on "click" this.cancel}}>
          {{svgJar "close-rounded" height="12" width="12"}}
        </button>
        <button type="button" class="btn btn--outline" data-test-update-button {{on "click" this.update}}>
          {{svgJar "checkmark-darkgray" height="12" width="12"}}
        </button>
      </div>
    {{else}}
      {{@title}}
      {{#unless @readOnly}}
        <button type="button" class="btn btn--outline ml-2 !px-3 !py-2" data-test-edit-button {{on "click" this.edit}}>
          {{svgJar "pencil-dark-full"}}
        </button>
      {{/unless}}
    {{/if}}
  </template>
}
